import { IconModule360, IconModuleObjectives, IconModuleReviews, IconTeam } from 'featherico';
import { flatten, groupBy, mapValues } from 'lodash';
import type { CYCLE, CYCLE_SPECIFIC_REVIEWEES, SEARCH, TEAM } from '.';
import type { CurrentUserDto } from '../../../../types/UserDtos';
import replacement from '../../../react/services/Replacement';
import * as R from '../../../routes';
import { getCurrentUser } from '../CurrentUser';
import { interpolateAppUrl } from '../Url';
import { hasBasicHr, isManager } from '../User';

interface Page {
    type: string;
    value: {
        name: string;
        url: string;
        icon: React.ReactNode;
    };
}

type SearchMode = typeof CYCLE_SPECIFIC_REVIEWEES | typeof TEAM | typeof CYCLE | typeof SEARCH;
interface Result {
    id: string;
    type: SearchMode;
    value: {
        name: string;
        url: string;
        type: 'USER' | 'OBJECTIVE' | 'REVIEW' | 'FEEDBACK';
    };
}

export function getPages(mode: SearchMode, items: Result[]) {
    if (mode === 'SEARCH') {
        return getPagesByItems(items);
    } else {
        return getPagesByMode(mode);
    }
}

function getPagesByItems(items: Result[]) {
    if (items.length === 0) {
        const user = getCurrentUser();
        const showCycles = hasBasicHr(user) || isManager(user);
        return flatten([getTeamPages(), showCycles ? getCyclePages() : []]);
    }

    const distribution = mapValues(
        groupBy(items, x => x.value.type),
        x => x.length
    );
    const pages = [];

    if (distribution.OBJECTIVE > 0) {
        pages.push(getObjectivePages());
    }
    if (distribution.FEEDBACK > 0) {
        pages.push(getFeedbackPages());
    }
    if (distribution.REVIEW > 0) {
        pages.push(getReviewPages());
    }
    if (distribution.USER > 0) {
        pages.push(getTeamPages());
    }

    return flatten(pages);
}

function getPagesByMode(mode: SearchMode) {
    return {
        TEAM: getTeamPages(),
        CYCLE: getCyclePages(),
        CYCLE_SPECIFIC_REVIEWEES: getReviewPages(),
        SEARCH: [],
    }[mode];
}

function getTeamPages(): Page[] {
    return [
        {
            type: 'PAGE',
            value: {
                name: `${replacement('company.big')} Directory`,
                url: interpolateAppUrl(R.PEOPLE_DIRECTORY, {}),
                icon: <IconTeam />,
            },
        },
    ];
}

function getCyclePages(): Page[] {
    return flatten([getObjectivePages(), getReviewPages(), getFeedbackPages()]);
}

function getObjectivePages(): Page[] {
    if (!getCurrentUser().company.enabledFeatures.objective) {
        return [];
    }

    return [
        {
            type: 'PAGE',
            value: getObjectiveOverview(getCurrentUser()),
        },
    ];
}

function getReviewPages(): Page[] {
    if (!getCurrentUser().company.enabledFeatures.review) {
        return [];
    }

    return [
        {
            type: 'PAGE',
            value: getReviewOverview(getCurrentUser()),
        },
    ];
}

function getFeedbackPages(): Page[] {
    if (!getCurrentUser().company.enabledFeatures.feedback) {
        return [];
    }

    return [
        {
            type: 'PAGE',
            value: getFeedbackOverview(getCurrentUser()),
        },
    ];
}

function getObjectiveOverview(user: CurrentUserDto): Page['value'] {
    if (hasBasicHr(user)) {
        return {
            name: replacement('objective.singular.big') + ' Cycles',
            url: interpolateAppUrl(R.OBJECTIVES_COMPANY_CYCLES, {}),
            icon: <IconModuleObjectives />,
        };
    } else if (isManager(user)) {
        return {
            name: replacement('objective.singular.big') + ' Team Overview',
            url: interpolateAppUrl(R.OBJECTIVES_TEAM_CYCLES, {}),
            icon: <IconModuleObjectives />,
        };
    } else {
        return {
            name: replacement('objective.singular.big') + ' Browser',
            url: interpolateAppUrl(R.OBJECTIVES_TEAM_BROWSE, {}),
            icon: <IconModuleObjectives />,
        };
    }
}

function getReviewOverview(user: CurrentUserDto): Page['value'] {
    if (hasBasicHr(user)) {
        return {
            name: replacement('pr.singular.short') + ' Cycles',
            url: interpolateAppUrl(R.ADMIN_REVIEWS_SELECT_CYCLE, {}),
            icon: <IconModuleReviews />,
        };
    } else {
        return {
            name: replacement('pr.singular.short') + ' Team Overview',
            url: interpolateAppUrl(R.REVIEWS_TEAM, {}),
            icon: <IconModuleReviews />,
        };
    }
}

function getFeedbackOverview(user: CurrentUserDto): Page['value'] {
    if (hasBasicHr(user)) {
        return {
            name: replacement('fr.short.big') + ' Cycles',
            url: interpolateAppUrl(R.ADMIN_FEEDBACK_SELECT_CYCLE, {}),
            icon: <IconModule360 />,
        };
    } else {
        return {
            name: replacement('fr.short.big') + ' Team Overview',
            url: interpolateAppUrl(R.FEEDBACK_USER_TEAM_CYCLES, {}),
            icon: <IconModule360 />,
        };
    }
}
